/**
 * Parse document.cookie into a key-value object.
 * Works only in browser environment.
 */
export function parseCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {};

  return Object.fromEntries(
    document.cookie
      .split(';')
      .map((c) => c.trim().split('='))
      .filter(([k]) => k)
      .map(([k, v]) => [k, decodeURIComponent(v ?? '')])
  );
}

/**
 * Get a specific cookie value by name.
 * Returns null if cookie doesn't exist or not in browser.
 */
export function getCookie(name: string): string | null {
  const cookies = parseCookies();
  return cookies[name] ?? null;
}

/**
 * Set a cookie with the given name, value, and options.
 */
export function setCookie(
  name: string,
  value: string,
  options: {
    path?: string;
    maxAge?: number;
    sameSite?: 'Strict' | 'Lax' | 'None';
    secure?: boolean;
  } = {}
): void {
  if (typeof document === 'undefined') return;

  const { path = '/', maxAge, sameSite = 'Lax', secure } = options;

  let cookie = `${name}=${encodeURIComponent(value)}; Path=${path}; SameSite=${sameSite}`;

  if (maxAge !== undefined) {
    cookie += `; Max-Age=${maxAge}`;
  }

  if (secure) {
    cookie += '; Secure';
  }

  document.cookie = cookie;
}

/**
 * Delete a cookie by setting its max-age to 0.
 */
export function deleteCookie(name: string, path = '/'): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Lax`;
}
